import * as React from 'react';
import { createContext, useContext, ReactNode } from 'react';
import useAuth from '../hooks/useAuth';
import TenantSelectionModal from '../components/TenantSelectionModal';
import { Tenant } from '../components/TenantSelectionModal';

/**
 * Interface for user object structure
 */
export interface User {
  name?: string;
  is_superuser?: boolean;
  is_platform_admin?: boolean;
  tenant_list?: Tenant[];
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Complete interface for the auth context return value
 */
export interface AuthContextReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  needsTenant: boolean;
  needsTenantSelection: boolean;
  selectedTenant: Tenant | null;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  authError: string;
  checkAuth: () => Promise<boolean>;
  selectTenant: (tenant: Tenant) => void;
  logout: () => void;
}

/**
 * Props interface for AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode;
}

// Create the auth context
const AuthContext = createContext<AuthContextReturn | null>(null);

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  
  return (
    <AuthContext.Provider value={auth}>
      {auth.needsTenantSelection && (
        <TenantSelectionModal 
          tenants={auth.user?.tenant_list} 
          onSelect={auth.selectTenant} 
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuthContext = (): AuthContextReturn => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 